import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Container,
  Grid,
  Skeleton,
  Stack,
} from "@mui/material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";

// Blockchain Services & Web3
import cinstance from "../../../Service/randomcinstance";
import web3 from "../../../Service/web3";
import {
  networkID,
  sumWeiToEther,
  findLowestWeiToEther,
  compareWei,
} from "../../../../static/utils";
import { getNetworkCurrency, DEFAULT_CURRENCY } from "../../../../static/Constants";

// Theme & Common UI
import NavBar from "../../../Components/NavBar";
import { colors, borderRadiusPx } from "../../../../theme";

// Subcomponents
import MineHeroBanner from "./components/MineHeroBanner";
import MineLotteryFilters from "./components/MineLotteryFilters";
import MineLotteryCard from "./components/MineLotteryCard";
import MineLotteryEmptyState from "./components/MineLotteryEmptyState";
import MineHowToPlay from "./components/MineHowToPlay";

import "./MineLotteryStyle.css";

const withTimeout = (promise, ms = 4500) => {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("RPC Request Timeout")), ms)
    ),
  ]);
};

const MineLottery = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [networkId, setNetworkId] = useState(networkID);
  const [lotteryList, setLotteryList] = useState([]);
  const [partnerMap, setPartnerMap] = useState({});
  const [partnerLogoData, setPartnerLogoData] = useState([]);

  // Filters & Sorting
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("newest");

  const apiUrl = process.env.REACT_APP_API_KEY;
  const currency = useMemo(
    () => getNetworkCurrency(networkId) || DEFAULT_CURRENCY,
    [networkId]
  );

  /**
   * Fetch All Live & Active MineBET (Type 1) Lotteries
   */
  const fetchMineLotteries = useCallback(
    async (isRefresh = false) => {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const watchdog = setTimeout(() => {
        setLoading(false);
        setRefreshing(false);
      }, 7000);

      try {
        if (window.ethereum) {
          window.web3 = web3;
          if (window.ethereum.networkVersion) {
            setNetworkId(window.ethereum.networkVersion);
          } else if (web3 && web3.eth && web3.eth.net && web3.eth.net.getId) {
            withTimeout(web3.eth.net.getId(), 2000)
              .then((netId) => {
                if (netId) setNetworkId(netId);
              })
              .catch(() => {});
          }
        }

        // 1. Get total lottery IDs count
        const totalIdsRaw = await withTimeout(
          cinstance.methods.lotteryId().call(),
          4000
        ).catch((err) => {
          console.warn("[MineBET] lotteryId() call failed:", err);
          return 0;
        });

        const totalIds = Number(totalIdsRaw || 0);
        const lotteryCalls = [];

        // In blockchain contract, lotteries are indexed from 1 up to totalIds
        for (let i = 1; i <= totalIds; i++) {
          lotteryCalls.push(
            withTimeout(cinstance.methods.lottery(i).call(), 4000).catch((err) => {
              console.log("lottery", i, err);
              return null;
            })
          );
        }

        const lotteryResults = await Promise.all(lotteryCalls);

        // Filter strictly for MineBET lotteries (Type 1) with active/open status (0 or 5)
        const filteredLotteries = lotteryResults
          .filter(
            (response) =>
              response &&
              (response.status === "0" || response.status === "5") &&
              Number(response.lotteryType) === 1
          )
          .sort((a, b) => Number(b.lotteryId) - Number(a.lotteryId));

        setLotteryList(filteredLotteries);

        // 2. Fetch Partner profiles concurrently for unique valid addresses
        const uniquePartners = [
          ...new Set(
            filteredLotteries
              .map((item) => item.partnerAddress || item.partner)
              .filter(
                (addr) =>
                  addr &&
                  addr !== "0x0000000000000000000000000000000000000000" &&
                  addr !== "0x0"
              )
          ),
        ];

        if (uniquePartners.length > 0) {
          const partnerPromises = uniquePartners.map((addr) =>
            withTimeout(cinstance.methods.partnerbyaddr(addr).call(), 3500)
              .then((res) => ({ [addr]: res }))
              .catch((err) => {
                console.log("partnerbyaddr error", err);
                return { [addr]: null };
              })
          );

          const partnerEntries = await Promise.all(partnerPromises);
          const mergedMap = partnerEntries.reduce(
            (acc, curr) => Object.assign(acc, curr),
            {}
          );
          setPartnerMap(mergedMap);
        } else {
          setPartnerMap({});
        }

        if (isRefresh) {
          toast.success("Active Minefield games refreshed!");
        }
      } catch (err) {
        console.error("[MineBET] Error fetching lotteries:", err);
        toast.error("Failed to load on-chain mine lotteries.");
      } finally {
        clearTimeout(watchdog);
        setLoading(false);
        setRefreshing(false);
      }
    },
    []
  );

  // Fetch S3 Partner Logos
  useEffect(() => {
    let isMounted = true;
    if (apiUrl) {
      axios
        .get(`${apiUrl}/s3imageupload/getAllPartnerLogos`)
        .then((response) => {
          if (isMounted) {
            const partnerData =
              response?.data?.result || response?.data || [];
            setPartnerLogoData(Array.isArray(partnerData) ? partnerData : []);
          }
        })
        .catch((err) => {
          console.log("partner logos error:", err);
          if (isMounted) setPartnerLogoData([]);
        });
    }
    return () => {
      isMounted = false;
    };
  }, [apiUrl]);

  useEffect(() => {
    fetchMineLotteries();
  }, [fetchMineLotteries]);

  /**
   * Computed Live Stats
   */
  const { combinedPrize, lowestEntry } = useMemo(() => {
    const prizeList = lotteryList.map((item) => item.totalPrize).filter(Boolean);
    const entryList = lotteryList.map((item) => item.entryFee).filter(Boolean);

    return {
      combinedPrize: sumWeiToEther(prizeList, 2),
      lowestEntry: findLowestWeiToEther(entryList, 3),
    };
  }, [lotteryList]);

  /**
   * Filtered & Sorted Lotteries for Display
   */
  const filteredLotteries = useMemo(() => {
    let list = [...lotteryList];

    // Search Query Filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter((item) => {
        const idMatch = `${item.lotteryId}`.includes(q);
        const partnerAddr = (item.partnerAddress || item.partner || "").toLowerCase();
        const partnerInfo = partnerMap[item.partnerAddress || item.partner];
        const partnerNameMatch =
          partnerInfo &&
          ((partnerInfo.name && partnerInfo.name.toLowerCase().includes(q)) ||
            (partnerInfo.companyName &&
              partnerInfo.companyName.toLowerCase().includes(q)));
        const addrMatch = partnerAddr.includes(q);

        return idMatch || partnerNameMatch || addrMatch;
      });
    }

    // Sort options
    list.sort((a, b) => {
      const idA = Number(a.lotteryId || 0);
      const idB = Number(b.lotteryId || 0);
      const prizeComp = compareWei(a.totalPrize, b.totalPrize);
      const feeComp = compareWei(a.entryFee, b.entryFee);
      const capA = Number(a.capacity || 0);
      const capB = Number(b.capacity || 0);

      switch (sortBy) {
        case "oldest":
          return idA - idB;
        case "highestPrize":
          return -prizeComp || idB - idA;
        case "lowestEntry":
          return feeComp || idA - idB;
        case "capacityHigh":
          return capB - capA || idB - idA;
        case "capacityLow":
          return capA - capB || idA - idB;
        case "newest":
        default:
          return idB - idA;
      }
    });

    return list;
  }, [lotteryList, searchQuery, sortBy, partnerMap]);

  const handleResetFilters = () => {
    setSearchQuery("");
    setSortBy("newest");
  };

  const hasActiveFilters = searchQuery.trim() !== "" || sortBy !== "newest";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: colors.dashboard.canvas,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <ToastContainer position="top-right" autoClose={3000} theme="colored" />
      <NavBar />

      <Container
        maxWidth="xl"
        className="mine-page-container"
        sx={{ pt: { xs: "90px", md: "100px" }, pb: 8, flexGrow: 1 }}
      >
        {/* 1. Hero Header & Live Stats Banner */}
        <MineHeroBanner
          totalActive={lotteryList.length}
          combinedPrize={combinedPrize}
          lowestEntry={lowestEntry}
          currency={currency}
        />

        {/* 2. Filter, Search & Sorting Controls */}
        <MineLotteryFilters
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          sortBy={sortBy}
          onSortChange={setSortBy}
          onRefresh={() => fetchMineLotteries(true)}
          loading={loading || refreshing}
        />

        {/* 3. Loading Skeletons */}
        {loading && (
          <Grid container spacing={3}>
            {[1, 2, 3].map((idx) => (
              <Grid item xs={12} sm={6} lg={4} key={`mine-skel-${idx}`}>
                <Box
                  sx={{
                    bgcolor: colors.dashboard.cardBg,
                    borderRadius: borderRadiusPx.xl,
                    border: `1px solid ${colors.dashboard.cardBorder}`,
                    p: 3,
                    height: 480,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Skeleton variant="rounded" width={36} height={36} sx={{ borderRadius: borderRadiusPx.md }} />
                    <Box sx={{ flexGrow: 1 }}>
                      <Skeleton variant="text" width="60%" height={24} />
                      <Skeleton variant="text" width="40%" height={16} />
                    </Box>
                  </Stack>
                  <Skeleton
                    variant="rounded"
                    height={40}
                    sx={{ borderRadius: borderRadiusPx.md }}
                  />
                  <Skeleton
                    variant="rounded"
                    height={100}
                    sx={{ borderRadius: borderRadiusPx.lg }}
                  />
                  <Skeleton
                    variant="rounded"
                    height={80}
                    sx={{ borderRadius: borderRadiusPx.md }}
                  />
                  <Skeleton
                    variant="rounded"
                    height={44}
                    sx={{ borderRadius: borderRadiusPx.md }}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        )}

        {/* 4. Empty State */}
        {!loading && filteredLotteries.length === 0 && (
          <MineLotteryEmptyState
            onReset={handleResetFilters}
            hasActiveFilters={hasActiveFilters}
          />
        )}

        {/* 5. Active Minefields Grid */}
        {!loading && filteredLotteries.length > 0 && (
          <Grid container spacing={3}>
            {filteredLotteries.map((item) => {
              const partnerAddr = item.partnerAddress || item.partner;
              const partnerData = partnerMap[partnerAddr] || null;

              return (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  lg={4}
                  key={`mine-lottery-${item.lotteryId}`}
                >
                  <MineLotteryCard
                    lottery={item}
                    partner={partnerData}
                    partnerLogo={partnerLogoData}
                    currency={currency}
                  />
                </Grid>
              );
            })}
          </Grid>
        )}

        {/* 6. How To Play & FAQ Guide */}
        <MineHowToPlay />
      </Container>
    </Box>
  );
};

export default MineLottery;
